import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { isTotpRequired, useLogin, useMe } from "@/api/auth";
import { LangSwitcher } from "@/components/LangSwitcher";
import { useT } from "@/lib/i18n";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const me = useMe();
  const login = useLogin();
  const t = useT();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needTotp, setNeedTotp] = useState(false);

  // Already logged in → bounce home (or wherever they came from).
  if (me.data) {
    return <Navigate to={location.state?.from ?? "/"} replace />;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const code = needTotp ? totpCode.replace(/\D/g, "").slice(0, 6) : undefined;
      await login.mutateAsync({
        login: username,
        password,
        ...(code ? { totp_code: code } : {}),
      });
      navigate(location.state?.from ?? "/", { replace: true });
    } catch (err) {
      // First wrong/missing code → server flips us into the TOTP-asking
      // mode. Subsequent failures replace the field with a fresh prompt.
      const e = err as { detail?: unknown };
      if (e && isTotpRequired(e.detail)) {
        setNeedTotp(true);
        setTotpCode("");
      }
    }
  };

  const errorMessage =
    login.error &&
    (() => {
      const d = login.error.detail;
      if (typeof d === "string") return d;
      if (
        typeof d === "object" &&
        d !== null &&
        "message" in d &&
        typeof (d as { message: unknown }).message === "string"
      ) {
        return (d as { message: string }).message;
      }
      return "Login failed";
    })();

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-muted p-4">
      <div className="card relative w-full max-w-md p-8">
        <div className="absolute right-3 top-3">
          <LangSwitcher />
        </div>
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-slate-800">NS Cabinet</h1>
          <p className="mt-1 text-sm text-slate-500">{t("login.title")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" autoComplete="on">
          <div>
            <label className="label" htmlFor="login">
              {t("login.login_label")}
            </label>
            <input
              id="login"
              name="username"
              type="text"
              autoComplete="username"
              className="input"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              {t("login.password_label")}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              className="input"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {needTotp && (
            <div>
              <label className="label" htmlFor="totp_code">
                {t("login.totp_label")}
              </label>
              <input
                id="totp_code"
                name="totp_code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="123 456"
                className="input text-center tracking-[0.4em]"
                required
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
            </div>
          )}

          {errorMessage && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={login.isPending || (needTotp && !/^\d{6}$/.test(totpCode))}
          >
            {login.isPending
              ? t("login.cta_busy")
              : t("login.cta")}
          </button>
        </form>
      </div>
    </div>
  );
}
